import React, { useEffect, useState, ReactElement } from "react";
import revampData from "../../data/revampData.json";
import { reorderByOrderNo } from "../../config/constants";
import { landingPageCircularAnimationimages } from "../../config/constants";
import { theme, websiteColors } from "../../themes/Theme";
import { useIsMobile } from "../../config/customHooks";

interface FeatureSection {
  title: string;
  features: string[];
}

interface RevampFeatureHighlight {
  label: string;
  title: string;
}

const CircleAnimation: React.FC = (): ReactElement => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [clicked, setClicked] = useState<boolean>(false);

  const isMobile: boolean = useIsMobile(600);

  // Extract and type data from revampData.json
  const featuresData: FeatureSection[] =
    revampData.data.listFeaturesPageHighlight.data;
  const featuresIntroData: RevampFeatureHighlight[] =
    revampData.data.listRevampFeatureHighlight.data;

  const sections: any = reorderByOrderNo(featuresData as any);

  const handleClick = (index: number, title: string): void => {
    setActiveIndex(index);
    setClickedIndex(index);
    setClicked(true);
    setTimeout(() => {
      setClicked(false);
      setClickedIndex(null);
    }, 320);
  };

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % 4);
    }, 1000);

    return () => clearInterval(interval);
  }, [isHovered]);

  const getCardClassName = (index: number, baseClass: string): string => {
    const isActive = activeIndex === index;
    const isClicked = clicked && clickedIndex === index;
    return `info-card ${isActive ? baseClass : ""} ${
      isClicked ? "card-clicked" : ""
    }`;
  };

  return (
    <div className="feature-main-container font-figtree mt-3 mb-5">
      <div className="text-center text-white fw-medium px-sm-5 px-3 py-5">
        <h6 className="text-uppercase heading-label mb-2">
          {featuresIntroData[0].label}
        </h6>
        <h2 className="display-5 main-heading fw-medium">
          {featuresIntroData[0].title}
        </h2>
      </div>
      <div className="three-column-layout">
        <div className="left-column">
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={getCardClassName(0, "landing-page-info-cardZero")}
            style={{
              border:
                activeIndex === 0
                  ? `3px solid ${websiteColors[theme].primaryWebsiteTheme}`
                  : `3px solid #fff`,
            }}
          >
            {renderCard(sections[0], handleClick, 0)}
          </div>
          {!isMobile ? (
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={getCardClassName(3, "landing-page-info-cardThree")}
              style={{
                border:
                  activeIndex === 3
                    ? `3px solid ${websiteColors[theme].primaryWebsiteTheme}`
                    : `3px solid #fff`,
              }}
            >
              {renderCard(sections[3], handleClick, 3)}
            </div>
          ) : (
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={getCardClassName(1, "landing-page-info-cardOne")}
              style={{
                border:
                  activeIndex === 1
                    ? `3px solid ${websiteColors[theme].primaryWebsiteTheme}`
                    : `3px solid #fff`,
              }}
            >
              {renderCard(sections[1], handleClick, 1)}
            </div>
          )}
        </div>

        <div className="circle-wrapper d-flex justify-content-center align-items-center m-auto">
          <div>
            <div
              style={{
                width: "100px",
                height: "100px",
                backgroundColor: clicked ? "#002E6E" : "transparent",
              }}
              className={`feature-revcontainer middle-circle-cont ${
                clicked ? "circle-zoom-on-click" : ""
              }`}
            ></div>
          </div>
          <div className="circle-container">
            {landingPageCircularAnimationimages?.map(
              (img, index: number) => (
                <img
                  key={index}
                  src={activeIndex === index ? img.active : img.normal}
                  className={`slice ${img.className}`}
                  alt={`slice ${index + 1}`}
                />
              )
            )}
          </div>
          <div className="kh-white-main-image-cont">
            <img
              className="kh-white-image-ani"
              src="https://d2nnrpx7gk2my2.cloudfront.net/files/680f266c428905000879ef32/WhiteLogo.svg"
              alt=""
            />
          </div>
        </div>

        <div className="right-column">
          {!isMobile ? (
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={getCardClassName(1, "landing-page-info-cardOne")}
              style={{
                border:
                  activeIndex === 1
                    ? `3px solid ${websiteColors[theme].primaryWebsiteTheme}`
                    : `3px solid #fff`,
              }}
            >
              {renderCard(sections[1], handleClick, 1)}
            </div>
          ) : (
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={getCardClassName(3, "landing-page-info-cardThree")}
              style={{
                border:
                  activeIndex === 3
                    ? `3px solid ${websiteColors[theme].primaryWebsiteTheme}`
                    : `3px solid #fff`,
              }}
            >
              {renderCard(sections[3], handleClick, 3)}
            </div>
          )}
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={getCardClassName(2, "landing-page-info-cardTwo")}
            style={{
              border:
                activeIndex === 2
                  ? `3px solid ${websiteColors[theme].primaryWebsiteTheme}`
                  : `3px solid #fff`,
            }}
          >
            {renderCard(sections[2], handleClick, 2)}
          </div>
        </div>
      </div>
      <style jsx>{`
        .three-column-layout {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          position: relative;
          max-width: 987px;
          margin: 0 auto;
        }
        .feature-revcontainer {
          transition: transform 1s ease-in-out, transform-origin 2s ease-in-out,
            background-color 0.3s ease;
          width: 100%;
          height: 100%;
          border-radius: 100%;
          background-position: center;
        }
        .middle-circle-cont {
          display: flex;
          justify-content: center;
          align-items: center;
          left: 169px;
          position: absolute;
          bottom: 154px;
        }

        .left-column,
        .right-column {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
        .feature-main-container {
          background-color: #002e6e;
          padding: 20px 15px 55px;
        }
        .landing-page-info-cardOne {
          background-color: #1fa5e8 !important;
          border: 3px solid #fff !important;
        }
        .zoom-rotate {
          transform: scale(15);
          background-color: #002e6e;
        }

        .landing-page-info-cardZero {
          background-color: #61c0ff !important;
          color: #fff !important;
          border: 3px solid #fff !important;
        }
        .landing-page-info-cardTwo {
          background-color: #793fdb !important;
          color: #fff !important;
          border: 3px solid #fff !important;
        }
        .landing-page-info-cardThree {
          background-color: #443fdb !important;
          color: #fff !important;
          border: 3px solid #fff !important;
        }

        .card-clicked {
          animation: cardPulse 0.32s ease;
          transform-origin: center;
        }

        @keyframes cardPulse {
          0% {
            transform: scale(1);
            filter: brightness(1);
          }
          50% {
            transform: scale(1.02);
            filter: brightness(1.2);
          }
          100% {
            transform: scale(1);
            filter: brightness(1);
          }
        }

        .circle-wrapper {
          position: relative;
          width: 300px;
          height: 300px;
          right: 60px;
        }
        .kh-white-main-image-cont {
          position: absolute;
          left: 178px;
          bottom: 159px;
        }

        .circle-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 280px;
          height: 280px;
        }
        .info-card {
          box-shadow: 0px 0px 30px 0px #002e6e33;
          border-radius: 15px;
          padding: 16px;
          background-color: #002e6e;
          color: #fff !important;
          min-height: 235px;
          height: 100%;
          max-height: 500px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .slice {
          position: absolute;
          width: 50%;
          height: 50%;
          top: 25%;
          left: 25%;
          transform-origin: center center;
          transition: all 0.5s ease;
        }

        .rotate-0 {
          transform: rotate(362deg);
          left: 87px;
          top: -48px;
        }

        .rotate-90 {
          transform: rotate(1deg);
          left: 73px;
          top: 69px;
        }

        .rotate-360 {
          left: 159.5px;
          height: 100px;
          width: 100px;
          top: 36px;
        }
        .rotate-180 {
          transform: rotate(2deg);
          left: 207px;
          top: -34px;
        }
        .rotate-270 {
          transform: rotate(362deg);
          left: 191px;
          top: 82px;
        }

        .kh-white-image-ani {
          width: 85px;
          height: 85px;
          border-radius: 50%;
          background-color: #002e6e;
        }

        @media (max-width: 1024px) {
          .three-column-layout {
            max-width: 600px;
          }

          .info-card {
            max-width: 150px;
            min-height: 79px;
            max-height: auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }
          .circle-wrapper {
            top: 69px;
            width: 248px;
          }
        }
        @media ((max-width: 1200px) and (min-width: 1024px)) {
          .three-column-layout {
            max-width: 960px;
          }
        }
        @media (max-width: 1022px) {
          .middle-circle-cont {
            max-width: 960px;
            left: 143px;
          }
          .kh-white-main-image-cont {
            position: absolute;
            left: 149px;
            bottom: 160px;
          }
        }
        @media (max-width: 768px) {
          .left-column,
          .right-column {
            flex-direction: row !important;
            gap: 2rem;
            width: 100%;
            justify-content: space-between;
            padding: 0 20px;
          }
          .three-column-layout {
            gap: 0.5rem !important;
          }
        }
        @media (max-width: 576px) {
          .right-column {
            padding: 30px 20px 5px;
          }
          .circle-wrapper {
            right: 70px;
          }
        }
      `}</style>
    </div>
  );
};

interface RenderCardProps {
  section: FeatureSection;
  handleClick: (index: number, title: string) => void;
  index: number;
}

const renderCard = (
  section: FeatureSection,
  handleClick: (index: number, title: string) => void,
  index: number
): ReactElement => {
  return (
    <div onClick={() => handleClick(index, section.title)}>
      <p className="new-feature-page-title mb-lg-1">{section.title}</p>
      <ul className="new-feature-page-title-ul d-block d-md-flex flex-column mb-0">
        {section.features.map((point: string, i: number) => (
          <li className="new-feature-page-title-ul-li" key={i}>
            {point}
          </li>
        ))}
      </ul>
      <style jsx>{`
        .new-feature-page-title {
          font-size: 18px;
          font-weight: 700;
          color: #fff !important;
        }
        .new-feature-page-title-ul-li {
          font-size: 14px;
          font-weight: 500;
          color: #fff !important;
        }
        .new-feature-page-title-ul {
          padding-left: 1.5rem !important;
        }

        @media (max-width: 1024px) {
          .new-feature-page-title-ul {
            display: none !important;
          }
          .new-feature-page-title {
            font-size: 14px;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default CircleAnimation;
